import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import { tagService } from '../../services/api';
import { toast } from 'react-toastify';
import { Plus, Search, Tag as TagIcon, Edit, Trash2, ExternalLink, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';

const TagList = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tagService.getTags({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: search || undefined,
        activeOnly: !showInactive,
        includePostCount: true
      });

      if (response.success) {
        setTags(response.data.tags);
        setPagination(response.data.pagination);
      } else {
        toast.error('Erro ao carregar tags: ' + response.message);
      }
    } catch (error) {
      console.error('Erro ao buscar tags:', error);
      toast.error('Falha ao carregar tags. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, search, showInactive]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Voltar para primeira página ao pesquisar
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleToggleInactive = () => {
    setShowInactive(prev => !prev);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleDeleteTag = async (id, tagName) => {
    if (!window.confirm(`Tem certeza que deseja excluir a tag "${tagName}"?`)) {
      return;
    }

    try {
      const response = await tagService.deleteTag(id);
      
      if (response.success) {
        toast.success('Tag excluída com sucesso');
        fetchTags();
      } else {
        toast.error(`Erro ao excluir tag: ${response.message}`);
      }
    } catch (error) {
      console.error('Erro ao excluir tag:', error);
      toast.error('Falha ao excluir tag. Tente novamente mais tarde.');
    }
  };

  return (
    <>
      <PageTitle 
        title="Gerenciar Tags" 
        description="Lista de todas as tags para gerenciamento"
      />
      <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Gerenciar Tags</h1>
          <Button variant="igreja" size="sm" asChild className="flex items-center gap-2">
            <Link to="/admin/tags/new">
              <Plus className="h-4 w-4" />
              Nova Tag
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Filtrar Tags</CardTitle>
            <CardDescription>
              Use os campos abaixo para filtrar a lista de tags
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar tags..."
                  value={search}
                  onChange={handleSearchChange}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="show-inactive" checked={showInactive} onCheckedChange={handleToggleInactive} />
                <label htmlFor="show-inactive" className="text-sm font-medium cursor-pointer">
                  Mostrar inativas
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

      {loading && tags.length === 0 ? (
        <Card className="mt-8">
          <CardContent className="p-8 flex justify-center">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue"></div>
              <p className="text-muted-foreground mt-4">Carregando tags...</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {tags.length === 0 ? (
            <Card className="mt-8">
              <CardContent className="p-8 text-center">
                <div className="flex flex-col items-center">
                  <TagIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">Nenhuma tag encontrada</h3>
                  <p className="text-muted-foreground">
                    Tente ajustar os filtros ou crie uma nova tag
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>Tags</CardTitle>
                <CardDescription>
                  {pagination.totalItems} {pagination.totalItems === 1 ? 'tag encontrada' : 'tags encontradas'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="py-3 px-4 text-left font-medium">Nome</th>
                        <th className="py-3 px-4 text-left font-medium hidden sm:table-cell">Cor</th>
                        <th className="py-3 px-4 text-left font-medium">Posts</th>
                        <th className="py-3 px-4 text-left font-medium">Status</th>
                        <th className="py-3 px-4 text-center font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tags.map(tag => (
                        <tr key={tag.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: tag.color || '#6B7280' }}
                              ></span>
                              <span className="font-medium">{tag.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 hidden sm:table-cell">
                            {tag.color || '-'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary" className="font-normal">
                              {tag.postCount !== undefined ? tag.postCount : '0'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {tag.isActive ? (
                              <Badge variant="success" className="flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                Ativa
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="flex items-center gap-1">
                                <X className="h-3 w-3" />
                                Inativa
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button size="sm" variant="ghost" asChild>
                                <Link to={`/tag/${tag.slug}`} target="_blank" title="Ver posts desta tag">
                                  <ExternalLink className="h-4 w-4" />
                                </Link>
                              </Button>
                              
                              <Button size="sm" variant="ghost" asChild>
                                <Link to={`/admin/tags/edit/${tag.id}`} title="Editar tag">
                                  <Edit className="h-4 w-4" />
                                </Link>
                              </Button>
                              
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeleteTag(tag.id, tag.name)}
                                disabled={tag.postCount > 0}
                                title={tag.postCount > 0 ? "Não é possível excluir tags utilizadas em posts" : "Excluir tag"}
                                className={tag.postCount > 0 ? "opacity-50 cursor-not-allowed" : "text-destructive hover:text-destructive"}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {pagination.totalPages > 1 && (
            <Card className="mt-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                  >
                    Anterior
                  </Button>

                  <div className="text-sm text-muted-foreground">
                    Página {pagination.currentPage} de {pagination.totalPages}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Próxima
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Loading overlay */}
          {loading && tags.length > 0 && (
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
              <Card className="p-6">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-church-blue"></div>
                  <span className="text-muted-foreground">Atualizando tags...</span>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
      </div>
    </div>
    </>
  );
};

export default TagList;
